import React from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Step, StepLabel,
  Stepper, Table, TableBody, TableCell, TableRow, TextField, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  contractTypeService, departmentService, employmentService, positionService, seniorityLevelService,
} from '../../services/api';
import { useToast } from '../Common/ToastProvider';
import { apiErrorText, calcSalary, fmtDate, fmtMoney } from './salaryFormat';

// S3 — new hire in one step: person + employee + labour contract. Nothing is saved before
// "Create" on the Review step; the server creates all three in one go.
// CEV-S3 — mode "Former employee (history)": the same data plus the leaving; the person is saved
// terminated from the leaving date, without access to HRApp.
const STEPS_NEW = ['Person', 'Employment', 'Contract', 'Review'];
const STEPS_FORMER = ['Person', 'Employment', 'Contract', 'Leaving', 'Review'];
const EMPTY = {
  firstName: '', middleName: '', lastName: '', personalEmail: '', phone: '', personalIdNumber: '',
  workEmail: '', positionId: '', seniorityLevelId: '', departmentId: '',
  contractNumber: '', contractTypeId: '', contractDate: '', startDate: '', jobTitleName: '', jobTitleCode: '',
  workplace: '', contractTerm: 'Indefinite', probationPeriodMonths: 6, annualLeaveDaysPerYear: 20,
  baseSalary: '', salaryCurrency: 'EUR', salaryBasis: 'Gross', contractDocumentPath: '',
  // CEV-S3 — leaving (former employee only)
  leftOn: '', legalBasis: '', requestDate: '', orderNumber: '', orderDate: '', terminationDocumentPath: '', terminationNote: '',
};

export default function NewHireWizard({ open, onClose, onCreated }) {
  const toast = useToast();
  const [step, setStep] = React.useState(0);
  const [mode, setMode] = React.useState('new');   // CEV-S3 — 'new' | 'former'
  const former = mode === 'former';
  const STEPS = former ? STEPS_FORMER : STEPS_NEW;
  const stepName = STEPS[step];
  const [form, setForm] = React.useState(EMPTY);
  const [lists, setLists] = React.useState({ positions: [], levels: [], departments: [], types: [] });
  const [error, setError] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setStep(0); setForm(EMPTY); setError(''); setMode('new');
    const safe = (p) => p.then((x) => (Array.isArray(x) ? x : [])).catch(() => []);
    Promise.all([safe(positionService.getAll()), safe(seniorityLevelService.getAll()),
      safe(departmentService.getAll()), safe(contractTypeService.getAll())])
      .then(([positions, levels, departments, types]) => setLists({ positions, levels, departments, types }));
  }, [open]);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));
  const nameOf = (list, id, field) => list.find((x) => String(x.id) === String(id))?.[field] || '—';
  const year = form.startDate ? Number(form.startDate.substring(0, 4)) : new Date().getFullYear();
  const calc = calcSalary(form.baseSalary, form.salaryCurrency, form.salaryBasis, year);

  const today = new Date().toISOString().substring(0, 10);
  const validByStep = {
    Person: form.firstName.trim() && form.lastName.trim(),
    Employment: form.workEmail.trim() && form.positionId && form.seniorityLevelId,
    // CEV-S3 — for a former employee the salary may be unknown (empty or 0)
    Contract: form.contractNumber.trim() && form.contractDate && form.startDate && form.jobTitleName.trim()
      && (former ? !(Number(form.baseSalary) < 0) : Number(form.baseSalary) > 0),
    Leaving: form.leftOn && form.legalBasis.trim() && form.leftOn >= form.startDate && form.leftOn <= today,
    Review: true,
  };
  const stepValid = STEPS.map((n) => validByStep[n]);

  const create = async () => {
    setSaving(true);
    setError('');
    try {
      const num = (v) => (v === '' || v == null ? null : Number(v));
      const result = await employmentService.newHire({
        ...form,
        middleName: form.middleName || null, personalEmail: form.personalEmail || null, phone: form.phone || null,
        personalIdNumber: form.personalIdNumber || null, positionId: Number(form.positionId),
        seniorityLevelId: Number(form.seniorityLevelId), departmentId: num(form.departmentId),
        contractTypeId: num(form.contractTypeId), probationPeriodMonths: Number(form.probationPeriodMonths) || 0,
        annualLeaveDaysPerYear: Number(form.annualLeaveDaysPerYear) || 20, baseSalary: Number(form.baseSalary) || 0,
        contractDocumentPath: form.contractDocumentPath || null,
        // CEV-S3 — the leaving of a former employee
        former: former ? {
          terminationDate: form.leftOn, legalBasis: form.legalBasis.trim(), requestDate: form.requestDate || null,
          orderNumber: form.orderNumber || null, orderDate: form.orderDate || null,
          documentPath: form.terminationDocumentPath || null, note: form.terminationNote || null,
        } : null,
      });
      toast(former ? 'Former employee registered' : 'Employee created', { variant: 'success' });
      onCreated?.(result, `${form.firstName} ${form.lastName}`.trim());
    } catch (err) {
      setError(apiErrorText(err, 'Could not create the employee.'));
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, props = {}) => (
    <TextField id={`nh-${name}`} fullWidth label={label} value={form[name]} onChange={set(name)} {...props} />
  );
  const dateField = (name, label, required) => field(name, label, { type: 'date', required, InputLabelProps: { shrink: true } });
  const select = (name, label, items, valueKey, textKey, required) => (
    <TextField id={`nh-${name}`} select fullWidth required={required} label={label} value={form[name]} onChange={set(name)}>
      {!required && <MenuItem value="">—</MenuItem>}
      {items.map((x) => <MenuItem key={x[valueKey]} value={x[valueKey]}>{x[textKey]}</MenuItem>)}
    </TextField>
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        {former ? 'Register former employee' : 'New hire'}
        {/* CEV-S3 — mode switch; changing it starts the steps again */}
        <ToggleButtonGroup size="small" exclusive value={mode}
          onChange={(_, v) => { if (v) { setMode(v); setStep(0); setError(''); } }}>
          <ToggleButton value="new">New hire</ToggleButton>
          <ToggleButton value="former">Former employee (history)</ToggleButton>
        </ToggleButtonGroup>
      </DialogTitle>
      <DialogContent>
        {former && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Saved for history only: terminated from the leaving date, no access to HRApp, not offered in projects, hours or equipment.
          </Alert>
        )}
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
        </Stepper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {stepName === 'Person' && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>{field('firstName', 'First name', { required: true })}</Grid>
            <Grid item xs={12} sm={4}>{field('middleName', 'Middle name')}</Grid>
            <Grid item xs={12} sm={4}>{field('lastName', 'Last name', { required: true })}</Grid>
            <Grid item xs={12} sm={6}>{field('personalEmail', 'Personal e-mail')}</Grid>
            <Grid item xs={12} sm={6}>{field('phone', 'Phone')}</Grid>
            <Grid item xs={12} sm={6}>{field('personalIdNumber', 'Personal ID number (ЕГН)', { type: 'password', autoComplete: 'off', helperText: 'Stored on the person and the contract; never shown in lists' })}</Grid>
          </Grid>
        )}

        {stepName === 'Employment' && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>{field('workEmail', former ? 'E-mail (work if known, otherwise any)' : 'Work e-mail', { required: true, placeholder: 'name.surname@inspirit.bg' })}</Grid>
            <Grid item xs={12} sm={6}>{select('departmentId', 'Department', lists.departments, 'id', 'name', false)}</Grid>
            <Grid item xs={12} sm={6}>{select('positionId', 'Position', lists.positions, 'id', 'title', true)}</Grid>
            <Grid item xs={12} sm={6}>{select('seniorityLevelId', 'Seniority', lists.levels, 'id', 'name', true)}</Grid>
          </Grid>
        )}

        {stepName === 'Contract' && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>{field('contractNumber', 'Contract number', { required: true })}</Grid>
            <Grid item xs={12} sm={6}>{select('contractTypeId', 'Contract type', lists.types, 'id', 'name', false)}</Grid>
            <Grid item xs={12} sm={6}>{dateField('contractDate', 'Signed on', true)}</Grid>
            <Grid item xs={12} sm={6}>{dateField('startDate', 'Start date', true)}</Grid>
            <Grid item xs={12} sm={8}>{field('jobTitleName', 'Job title (НКПД)', { required: true })}</Grid>
            <Grid item xs={12} sm={4}>{field('jobTitleCode', 'Code')}</Grid>
            <Grid item xs={12} sm={8}>{field('workplace', 'Workplace')}</Grid>
            <Grid item xs={12} sm={4}>{field('contractTerm', 'Term')}</Grid>
            <Grid item xs={6} sm={3}>{field('probationPeriodMonths', 'Probation (months)', { type: 'number' })}</Grid>
            <Grid item xs={6} sm={3}>{field('annualLeaveDaysPerYear', 'Annual leave (days)', { type: 'number' })}</Grid>
            <Grid item xs={12} sm={6}>{field('baseSalary', 'Salary', { type: 'number', required: !former, inputProps: { step: '0.01', min: 0 }, helperText: former ? 'Leave empty when not known' : undefined })}</Grid>
            <Grid item xs={6}>
              <TextField id="nh-currency" select fullWidth label="Currency" value={form.salaryCurrency} onChange={set('salaryCurrency')}>
                <MenuItem value="EUR">EUR</MenuItem><MenuItem value="BGN">BGN</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField id="nh-basis" select fullWidth label="Gross / Net" value={form.salaryBasis} onChange={set('salaryBasis')}>
                <MenuItem value="Gross">Gross</MenuItem><MenuItem value="Net">Net</MenuItem>
              </TextField>
            </Grid>
            {calc && (
              <Grid item xs={12}>
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 1, fontSize: 14 }}>
                  {form.salaryBasis === 'Net'
                    ? <>Calculated gross: <b>{fmtMoney(calc.gross, form.salaryCurrency)}</b></>
                    : <>Calculated net: <b>{fmtMoney(calc.net, form.salaryCurrency)}</b></>}
                </Box>
              </Grid>
            )}
            <Grid item xs={12}>{field('contractDocumentPath', 'Contract in dossier (path)')}</Grid>
          </Grid>
        )}

        {/* CEV-S3 — the leaving of a former employee */}
        {stepName === 'Leaving' && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>{dateField('leftOn', 'Terminated from', true)}</Grid>
            <Grid item xs={12} sm={8}>{field('legalBasis', 'Legal basis', { required: true, placeholder: 'чл. 326 КТ' })}</Grid>
            <Grid item xs={12} sm={4}>{dateField('requestDate', 'Employee request dated', false)}</Grid>
            <Grid item xs={12} sm={4}>{field('orderNumber', 'Order number')}</Grid>
            <Grid item xs={12} sm={4}>{dateField('orderDate', 'Order dated', false)}</Grid>
            <Grid item xs={12}>{field('terminationDocumentPath', 'Order in dossier (path)')}</Grid>
            <Grid item xs={12}>{field('terminationNote', 'Note')}</Grid>
            {form.leftOn && (form.leftOn < form.startDate || form.leftOn > today) && (
              <Grid item xs={12}>
                <Alert severity="error">The leaving date must be between the start date and today.</Alert>
              </Grid>
            )}
          </Grid>
        )}

        {stepName === 'Review' && (
          <Table size="small">
            <TableBody>
              <TableRow><TableCell>Name</TableCell><TableCell>{[form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ')}</TableCell></TableRow>
              <TableRow><TableCell>Work e-mail</TableCell><TableCell>{form.workEmail}</TableCell></TableRow>
              <TableRow><TableCell>Position · seniority · department</TableCell><TableCell>{nameOf(lists.positions, form.positionId, 'title')} · {nameOf(lists.levels, form.seniorityLevelId, 'name')} · {nameOf(lists.departments, form.departmentId, 'name')}</TableCell></TableRow>
              <TableRow><TableCell>Contract</TableCell><TableCell>No. {form.contractNumber} · signed {fmtDate(form.contractDate)} · start {fmtDate(form.startDate)} · {form.contractTerm}</TableCell></TableRow>
              <TableRow><TableCell>Job title</TableCell><TableCell>{form.jobTitleName} {form.jobTitleCode}</TableCell></TableRow>
              <TableRow><TableCell>Salary</TableCell><TableCell>{former && !(Number(form.baseSalary) > 0) ? 'not known' : <>{fmtMoney(form.baseSalary, form.salaryCurrency)} {form.salaryBasis.toLowerCase()}{calc ? ` (≈ ${fmtMoney(form.salaryBasis === 'Net' ? calc.gross : calc.net, form.salaryCurrency)} ${form.salaryBasis === 'Net' ? 'gross' : 'net'})` : ''}</>}</TableCell></TableRow>
              <TableRow><TableCell>Probation · leave</TableCell><TableCell>{form.probationPeriodMonths} months · {form.annualLeaveDaysPerYear} days</TableCell></TableRow>
              <TableRow><TableCell>Personal ID number</TableCell><TableCell>{form.personalIdNumber ? 'entered' : 'not entered'}</TableCell></TableRow>
              {former && (
                <TableRow><TableCell>Leaving</TableCell><TableCell>Terminated from {fmtDate(form.leftOn)} · {form.legalBasis}{form.requestDate ? ` · request ${fmtDate(form.requestDate)}` : ''}{form.orderNumber ? ` · order ${form.orderNumber}` : ''}{form.orderDate ? ` of ${fmtDate(form.orderDate)}` : ''}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>Back</Button>}
        {step < STEPS.length - 1
          ? <Button variant="contained" disabled={!stepValid[step]} onClick={() => setStep((s) => s + 1)}>Next: {STEPS[step + 1]}</Button>
          : <Button variant="contained" disabled={saving} onClick={create}>{former ? 'Register' : 'Create'}</Button>}
      </DialogActions>
    </Dialog>
  );
}
